import React, { useState, useEffect } from 'react';
import {
  X,
  Check,
  GripVertical,
  Trash2,
  Image as ImageIcon,
  Layers,
  Video,
  Edit3
} from 'lucide-react';
import { Spinner } from 'react-bootstrap';
import { ITripCardSlide } from '../../Models/trips/trip-summary.model';
import { ITripActivity } from '../../Models/trips/trip-activity.model';
import { ITripPhoto } from '../../services/trips/trip-photo.service';
import tripPhotoService from '../../services/trips/trip-photo.service';
import tripsService from '../../services/trips/trips.service';
import { TripImageSlideshow } from './trip-image-slideshow';
import styles from './trip-card-display-editor.module.scss';
import axios from 'axios';
import { getUrl } from '../../services/service-utils';
import authHeader from '../../services/auth-header';

// ── Types ─────────────────────────────────────────────────────────────────────

type Step =
  | 'cover'
  | 'activities'
  | 'trip_photos'
  | 'configure'
  | 'arrange'
  | 'preview';

/** A slide in the working build list (may be activity, trip cover, or trip photo) */
interface BuildSlide {
  /** Unique key for React rendering */
  key: string;
  slideType: 'image' | 'video_clip';
  sourceType: 'trip_cover' | 'activity' | 'trip_photo';
  /** Display label */
  label: string;
  /** The primary URL shown as thumbnail */
  imageUrl?: string;
  /** For activity slides — use the activity's display state thumbnail or imageUrl */
  activityId?: string;
  researchId?: string;
  videoId?: string;
  caption?: string;
  /** Per-slide mode override (only for activity slides) */
  overrideMode?: 'activity_display' | 'specific_image' | 'video_clip';
}

export interface TripCardDisplayEditorProps {
  tripId: string;
  tripTitle: string;
  tripImageUrl?: string;
  /** Optional: pass pre-loaded activities. If absent, the editor fetches them from the API. */
  activities?: ITripActivity[];
  /** Existing customized slides; undefined = editor starts from auto-generated list */
  initialSlides?: ITripCardSlide[];
  initialIncludeCover?: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /** Open the EditTripNameImageModal for changing the cover photo */
  onChangeCoverClick?: () => void;
}

// ── Step metadata ─────────────────────────────────────────────────────────────

const STEPS: Step[] = [
  'cover',
  'activities',
  'trip_photos',
  'configure',
  'arrange',
  'preview'
];
const STEP_LABELS = [
  'Cover',
  'Activities',
  'Photos',
  'Configure',
  'Arrange',
  'Preview'
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function activityThumbUrl(activity: ITripActivity): string | undefined {
  return (
    activity.displayState?.displayImages?.[0] ??
    activity.imageUrl ??
    activity.imageUrls?.[0]
  );
}

function activityToBuildSlide(activity: ITripActivity): BuildSlide {
  const thumb = activityThumbUrl(activity);
  const isVideo = activity.displayState?.displayMode === 'video_clip';
  return {
    key: `activity-${activity.id}`,
    slideType: isVideo ? 'video_clip' : 'image',
    sourceType: 'activity',
    label: activity.title,
    imageUrl: thumb,
    activityId: String(activity.id ?? ''),
    videoId: isVideo
      ? activity.linkedResearch?.find((r) => r.thumbnailUrl)?.id
      : undefined,
    overrideMode: 'activity_display'
  };
}

function buildSlideToCardSlide(s: BuildSlide): ITripCardSlide {
  return {
    activityId: s.activityId,
    slideType: s.slideType,
    imageUrl: s.imageUrl,
    researchId: s.researchId,
    videoId: s.videoId,
    caption: s.caption || undefined,
    sourceType: s.sourceType
  };
}

// ── Main Component ────────────────────────────────────────────────────────────

export function TripCardDisplayEditor({
  tripId,
  tripTitle,
  tripImageUrl,
  activities: activitiesProp,
  initialSlides,
  initialIncludeCover = true,
  onClose,
  onSaved,
  onChangeCoverClick
}: TripCardDisplayEditorProps) {
  const [step, setStep] = useState<Step>('cover');
  const [includeCover, setIncludeCover] = useState(initialIncludeCover);

  // Activities — use prop if provided, else fetch from API
  const [activities, setActivities] = useState<ITripActivity[]>(
    activitiesProp ?? []
  );
  const [loadingActivities, setLoadingActivities] = useState(
    !activitiesProp || activitiesProp.length === 0
  );

  // Which activities are selected for the slide list
  const [selectedActivityIds, setSelectedActivityIds] = useState<Set<string>>(
    () => {
      if (initialSlides) {
        const ids = new Set<string>();
        initialSlides.forEach((s) => {
          if (s.activityId) ids.add(s.activityId);
        });
        return ids;
      }
      // Default: all non-deleted from prop (may be empty if fetching async)
      return new Set(
        (activitiesProp ?? [])
          .filter((a) => !a.deleted)
          .map((a) => String(a.id ?? ''))
          .filter(Boolean)
      );
    }
  );

  // Unlinked trip photos (no placeId)
  const [unlinkedPhotos, setUnlinkedPhotos] = useState<ITripPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(
    new Set()
  );

  // Build list (the ordered working slide array)
  const [buildSlides, setBuildSlides] = useState<BuildSlide[]>(() =>
    initBuildSlides(
      initialSlides,
      activities,
      tripImageUrl,
      initialIncludeCover
    )
  );

  // Drag state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const [saving, setSaving] = useState(false);

  const stepIndex = STEPS.indexOf(step);

  // ── Load activities if not provided as prop ─────────────────────────────────
  useEffect(() => {
    if (activitiesProp && activitiesProp.length > 0) return;
    async function load() {
      setLoadingActivities(true);
      try {
        const trip = await tripsService.getTripById(tripId);
        const loaded: ITripActivity[] = trip.activities ?? [];
        setActivities(loaded);
        // Also seed the selection + buildSlides now that we have activities
        setSelectedActivityIds(
          new Set(
            loaded
              .filter((a) => !a.deleted)
              .map((a) => String(a.id ?? ''))
              .filter(Boolean)
          )
        );
        if (!initialSlides || initialSlides.length === 0) {
          setBuildSlides(
            initBuildSlides(undefined, loaded, tripImageUrl, includeCover)
          );
        }
      } catch (err) {
        console.error('[TripCardDisplayEditor] Failed to load activities', err);
      } finally {
        setLoadingActivities(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);

  // ── Load unlinked trip photos on mount ──────────────────────────────────────
  useEffect(() => {
    async function load() {
      setLoadingPhotos(true);
      try {
        const photos = await tripPhotoService.getTripPhotos(tripId);
        // Unlinked = photos with no place association
        const unlinked = photos.filter((p) => !p.latitude && !p.longitude);
        setUnlinkedPhotos(unlinked.length > 0 ? unlinked : photos.slice(0, 20));
      } catch (err) {
        console.error(
          '[TripCardDisplayEditor] Failed to load trip photos',
          err
        );
      } finally {
        setLoadingPhotos(false);
      }
    }
    load();
  }, [tripId]);

  // ── Sync cover change into buildSlides ──────────────────────────────────────
  useEffect(() => {
    setBuildSlides((prev) => {
      const hasCover = prev.some((s) => s.sourceType === 'trip_cover');
      if (includeCover && !hasCover && tripImageUrl) {
        return [
          {
            key: 'trip-cover',
            slideType: 'image' as const,
            sourceType: 'trip_cover' as const,
            label: 'Trip Cover',
            imageUrl: tripImageUrl
          },
          ...prev
        ];
      }
      if (!includeCover) {
        return prev.filter((s) => s.sourceType !== 'trip_cover');
      }
      return prev;
    });
  }, [includeCover, tripImageUrl]);

  // ── Navigation ──────────────────────────────────────────────────────────────

  function handleNext() {
    const order: Step[] = STEPS;
    const idx = order.indexOf(step);
    if (idx < order.length - 1) setStep(order[idx + 1]);
  }

  function handleBack() {
    const order: Step[] = STEPS;
    const idx = order.indexOf(step);
    if (idx > 0) setStep(order[idx - 1]);
  }

  // ── Activities step ─────────────────────────────────────────────────────────

  function toggleActivity(id: string) {
    setSelectedActivityIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  // When leaving the activities step, rebuild buildSlides to reflect selection
  function syncActivitiesToBuildSlides() {
    setBuildSlides((prev) => {
      const coverSlides = prev.filter((s) => s.sourceType === 'trip_cover');
      const photoSlides = prev.filter((s) => s.sourceType === 'trip_photo');
      const actSlides = activities
        .filter(
          (a) => !a.deleted && selectedActivityIds.has(String(a.id ?? ''))
        )
        .map(activityToBuildSlide);
      return [...coverSlides, ...actSlides, ...photoSlides];
    });
  }

  // ── Trip Photos step ────────────────────────────────────────────────────────

  function togglePhoto(id: string) {
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function addSelectedPhotos() {
    const toAdd = unlinkedPhotos.filter((p) => selectedPhotoIds.has(p.id));
    const newSlides: BuildSlide[] = toAdd.map((p) => ({
      key: `photo-${p.id}`,
      slideType: 'image' as const,
      sourceType: 'trip_photo' as const,
      label: p.caption || 'Trip Photo',
      imageUrl: p.photo_url ?? p.s3Path
    }));
    setBuildSlides((prev) => [...prev, ...newSlides]);
    setSelectedPhotoIds(new Set());
  }

  // ── Configure step ──────────────────────────────────────────────────────────

  function setSlideCaption(key: string, caption: string) {
    setBuildSlides((prev) =>
      prev.map((s) => (s.key === key ? { ...s, caption } : s))
    );
  }

  function setSlideOverrideMode(key: string, mode: BuildSlide['overrideMode']) {
    setBuildSlides((prev) =>
      prev.map((s) => (s.key === key ? { ...s, overrideMode: mode } : s))
    );
  }

  // ── Arrange step ────────────────────────────────────────────────────────────

  function handleDragStart(idx: number) {
    setDraggedIndex(idx);
  }

  function handleDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault();
    setDragOverIndex(idx);
  }

  function handleDrop(dropIdx: number) {
    if (draggedIndex === null || draggedIndex === dropIdx) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    const updated = [...buildSlides];
    const [item] = updated.splice(draggedIndex, 1);
    updated.splice(dropIdx, 0, item);
    setBuildSlides(updated);
    setDraggedIndex(null);
    setDragOverIndex(null);
  }

  function handleDragEnd() {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }

  function removeSlide(key: string) {
    setBuildSlides((prev) => prev.filter((s) => s.key !== key));
  }

  // ── Save ────────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaving(true);
    try {
      const slides = buildSlides.map(buildSlideToCardSlide);
      await axios.patch(
        `${getUrl()}trips/${tripId}/card-display`,
        { slides, includeCoverImage: includeCover },
        { headers: authHeader() }
      );
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      console.error('[TripCardDisplayEditor] Save failed', err);
    } finally {
      setSaving(false);
    }
  }

  // ── Preview slides ──────────────────────────────────────────────────────────

  const previewSlides: ITripCardSlide[] = buildSlides.map(
    buildSlideToCardSlide
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
    >
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2>Edit Trip Card</h2>
            <p className={styles.headerSub}>{tripTitle}</p>
          </div>
          <button
            type='button'
            className={styles.closeBtn}
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        {/* Step indicator */}
        <div className={styles.stepIndicator}>
          {STEPS.map((s, idx) => (
            <React.Fragment key={s}>
              <div className={styles.stepItem}>
                <div
                  className={`${styles.stepDot}${idx < stepIndex ? ` ${styles.stepDone}` : ''}${s === step ? ` ${styles.stepActive}` : ''}`}
                >
                  {idx < stepIndex ? <Check size={12} /> : idx + 1}
                </div>
                <span className={styles.stepLabel}>{STEP_LABELS[idx]}</span>
              </div>
              {idx < STEPS.length - 1 && <div className={styles.stepLine} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        <div className={styles.content}>
          {step === 'cover' && (
            <StepCover
              includeCover={includeCover}
              onToggle={setIncludeCover}
              tripImageUrl={tripImageUrl}
              onChangeCoverClick={onChangeCoverClick}
            />
          )}
          {step === 'activities' && (
            <StepActivities
              activities={activities}
              loading={loadingActivities}
              selectedIds={selectedActivityIds}
              onToggle={toggleActivity}
            />
          )}
          {step === 'trip_photos' && (
            <StepTripPhotos
              photos={unlinkedPhotos}
              loading={loadingPhotos}
              selectedIds={selectedPhotoIds}
              onToggle={togglePhoto}
              onAddSelected={addSelectedPhotos}
            />
          )}
          {step === 'configure' && (
            <StepConfigure
              slides={buildSlides}
              onCaptionChange={setSlideCaption}
              onOverrideModeChange={setSlideOverrideMode}
            />
          )}
          {step === 'arrange' && (
            <StepArrange
              slides={buildSlides}
              draggedIndex={draggedIndex}
              dragOverIndex={dragOverIndex}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              onRemove={removeSlide}
            />
          )}
          {step === 'preview' && <StepPreview slides={previewSlides} />}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button
            type='button'
            className={styles.btnSecondary}
            onClick={step === 'cover' ? onClose : handleBack}
          >
            {step === 'cover' ? 'Cancel' : '← Back'}
          </button>
          <div className={styles.footerRight}>
            {step === 'activities' && (
              <button
                type='button'
                className={styles.btnSecondary}
                onClick={() => {
                  syncActivitiesToBuildSlides();
                  handleNext();
                }}
              >
                Next →
              </button>
            )}
            {step !== 'preview' && step !== 'activities' && (
              <button
                type='button'
                className={styles.btnPrimary}
                onClick={handleNext}
              >
                Next →
              </button>
            )}
            {step === 'preview' && (
              <button
                type='button'
                className={styles.btnPrimary}
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Init helper ───────────────────────────────────────────────────────────────

function initBuildSlides(
  initialSlides: ITripCardSlide[] | undefined,
  activities: ITripActivity[],
  tripImageUrl: string | undefined,
  includeCover: boolean
): BuildSlide[] {
  if (initialSlides && initialSlides.length > 0) {
    return initialSlides.map((s, i) => ({
      key: `init-${i}-${s.activityId ?? s.sourceType ?? i}`,
      slideType: s.slideType,
      sourceType: s.sourceType ?? 'activity',
      label:
        s.sourceType === 'trip_cover'
          ? 'Trip Cover'
          : s.sourceType === 'trip_photo'
            ? 'Trip Photo'
            : (activities.find((a) => String(a.id) === s.activityId)?.title ??
              'Activity'),
      imageUrl: s.imageUrl ?? s.thumbnailUrl,
      activityId: s.activityId,
      researchId: s.researchId,
      videoId: s.videoId,
      caption: s.caption,
      overrideMode: 'activity_display'
    }));
  }

  // Auto-generate from activities
  const result: BuildSlide[] = [];
  if (includeCover && tripImageUrl) {
    result.push({
      key: 'trip-cover',
      slideType: 'image',
      sourceType: 'trip_cover',
      label: 'Trip Cover',
      imageUrl: tripImageUrl
    });
  }
  activities
    .filter((a) => !a.deleted)
    .forEach((a) => result.push(activityToBuildSlide(a)));
  return result;
}

// ── Step 1: Cover ─────────────────────────────────────────────────────────────

interface StepCoverProps {
  includeCover: boolean;
  onToggle: (v: boolean) => void;
  tripImageUrl?: string;
  onChangeCoverClick?: () => void;
}

function StepCover({
  includeCover,
  onToggle,
  tripImageUrl,
  onChangeCoverClick
}: StepCoverProps) {
  return (
    <div className={styles.coverStep}>
      <div className={styles.coverToggleRow}>
        <div className={styles.coverToggleLabel}>
          <strong>Include trip cover image</strong>
          <span>Show the trip's cover photo as the first slide</span>
        </div>
        <label className={styles.toggleSwitch}>
          <input
            type='checkbox'
            checked={includeCover}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <div className={styles.toggleTrack} />
          <div className={styles.toggleThumb} />
        </label>
      </div>

      {tripImageUrl && (
        <img
          src={tripImageUrl}
          alt='Trip cover'
          className={styles.coverPreview}
        />
      )}
      {!tripImageUrl && (
        <div className={styles.emptyState}>
          No cover image set for this trip.
        </div>
      )}

      {onChangeCoverClick && (
        <button
          type='button'
          className={styles.changeCoverBtn}
          onClick={onChangeCoverClick}
        >
          <Edit3 size={13} />
          Change cover photo
        </button>
      )}
    </div>
  );
}

// ── Step 2: Select Activities ─────────────────────────────────────────────────

interface StepActivitiesProps {
  activities: ITripActivity[];
  loading: boolean;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}

function StepActivities({
  activities,
  loading,
  selectedIds,
  onToggle
}: StepActivitiesProps) {
  if (loading) {
    return (
      <div className={styles.spinnerWrap}>
        <Spinner
          animation='border'
          size='sm'
        />
      </div>
    );
  }

  const visible = activities.filter((a) => !a.deleted);

  if (visible.length === 0) {
    return (
      <div className={styles.emptyState}>No activities on this trip yet.</div>
    );
  }

  return (
    <div className={styles.activitiesStep}>
      <p className={styles.sectionTitle}>
        Select which activities to include in the trip card slideshow
      </p>
      {visible.map((activity) => {
        const id = String(activity.id ?? '');
        const selected = selectedIds.has(id);
        const thumb = activityThumbUrl(activity);
        return (
          <div
            key={id}
            className={`${styles.activityRow}${selected ? ` ${styles.activityRowSelected}` : ''}`}
            onClick={() => onToggle(id)}
            role='checkbox'
            aria-checked={selected}
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onToggle(id)}
          >
            {thumb ? (
              <img
                src={thumb}
                alt=''
                className={styles.activityThumb}
              />
            ) : (
              <div className={styles.activityThumbPlaceholder}>
                <ImageIcon size={18} />
              </div>
            )}
            <div className={styles.activityInfo}>
              <strong>{activity.title}</strong>
              {activity.placeName && <span>{activity.placeName}</span>}
            </div>
            <div
              className={`${styles.activityCheck}${selected ? ` ${styles.activityCheckActive}` : ''}`}
            >
              {selected && <Check size={11} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Step 3: Trip Photos ───────────────────────────────────────────────────────

interface StepTripPhotosProps {
  photos: ITripPhoto[];
  loading: boolean;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onAddSelected: () => void;
}

function StepTripPhotos({
  photos,
  loading,
  selectedIds,
  onToggle,
  onAddSelected
}: StepTripPhotosProps) {
  if (loading) {
    return (
      <div className={styles.spinnerWrap}>
        <Spinner
          animation='border'
          size='sm'
        />
      </div>
    );
  }

  return (
    <div className={styles.tripPhotosStep}>
      <p className={styles.tripPhotosHelp}>
        These trip photos are not linked to a specific place. Select any you
        want to add as extra slides.
      </p>

      {photos.length === 0 ? (
        <div className={styles.emptyState}>No unlinked trip photos found.</div>
      ) : (
        <>
          <div className={styles.photoGrid}>
            {photos.map((p) => {
              const url = p.photo_url ?? p.s3Path;
              const sel = selectedIds.has(p.id);
              return (
                <div
                  key={p.id}
                  className={`${styles.photoTile}${sel ? ` ${styles.photoTileSelected}` : ''}`}
                  onClick={() => onToggle(p.id)}
                  role='checkbox'
                  aria-checked={sel}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && onToggle(p.id)}
                >
                  <img
                    src={url}
                    alt={p.caption ?? ''}
                    loading='lazy'
                  />
                  {sel && (
                    <div className={styles.photoTileCheck}>
                      <Check size={11} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            type='button'
            className={styles.addSelectedBtn}
            disabled={selectedIds.size === 0}
            onClick={onAddSelected}
          >
            <Check size={14} />
            Add {selectedIds.size > 0 ? selectedIds.size : ''} selected photo
            {selectedIds.size !== 1 ? 's' : ''} to slides
          </button>
        </>
      )}
    </div>
  );
}

// ── Step 4: Configure ─────────────────────────────────────────────────────────

interface StepConfigureProps {
  slides: BuildSlide[];
  onCaptionChange: (key: string, caption: string) => void;
  onOverrideModeChange: (key: string, mode: BuildSlide['overrideMode']) => void;
}

function StepConfigure({
  slides,
  onCaptionChange,
  onOverrideModeChange
}: StepConfigureProps) {
  if (slides.length === 0) {
    return (
      <div className={styles.emptyState}>
        No slides yet. Go back and select activities or photos.
      </div>
    );
  }

  return (
    <div className={styles.configStep}>
      {slides.map((slide) => {
        const modes: Array<{
          value: NonNullable<BuildSlide['overrideMode']>;
          label: string;
          icon: React.ReactNode;
        }> = [
          {
            value: 'activity_display',
            label: 'Activity Display',
            icon: <Layers size={12} />
          },
          {
            value: 'specific_image',
            label: 'Specific Image',
            icon: <ImageIcon size={12} />
          },
          {
            value: 'video_clip',
            label: 'Video Clip',
            icon: <Video size={12} />
          }
        ];

        return (
          <div
            key={slide.key}
            className={styles.configCard}
          >
            <div className={styles.configCardHeader}>
              {slide.imageUrl ? (
                <img
                  src={slide.imageUrl}
                  alt=''
                  className={styles.configThumb}
                />
              ) : (
                <div className={styles.configThumbPlaceholder}>
                  <ImageIcon size={14} />
                </div>
              )}
              <span>{slide.label}</span>
              <span className={styles.configLabel}>{slide.sourceType}</span>
            </div>
            <div className={styles.configCardBody}>
              {/* Only show mode buttons for activity slides */}
              {slide.sourceType === 'activity' && (
                <div className={styles.configModeRow}>
                  {modes.map((m) => (
                    <button
                      key={m.value}
                      type='button'
                      className={`${styles.configModeBtn}${slide.overrideMode === m.value ? ` ${styles.configModeBtnActive}` : ''}`}
                      onClick={() => onOverrideModeChange(slide.key, m.value)}
                    >
                      {m.icon}&nbsp;{m.label}
                    </button>
                  ))}
                </div>
              )}
              <input
                type='text'
                className={styles.configCaptionInput}
                placeholder='Caption (optional)'
                value={slide.caption ?? ''}
                onChange={(e) => onCaptionChange(slide.key, e.target.value)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Step 5: Arrange ───────────────────────────────────────────────────────────

interface StepArrangeProps {
  slides: BuildSlide[];
  draggedIndex: number | null;
  dragOverIndex: number | null;
  onDragStart: (idx: number) => void;
  onDragOver: (e: React.DragEvent, idx: number) => void;
  onDrop: (idx: number) => void;
  onDragEnd: () => void;
  onRemove: (key: string) => void;
}

function StepArrange({
  slides,
  draggedIndex,
  dragOverIndex,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onRemove
}: StepArrangeProps) {
  if (slides.length === 0) {
    return (
      <div className={styles.emptyState}>
        No slides to arrange. Go back to add activities or photos.
      </div>
    );
  }

  return (
    <div className={styles.arrangeList}>
      {slides.map((slide, idx) => (
        <div
          key={slide.key}
          className={`${styles.arrangeItem}${draggedIndex === idx ? ` ${styles.arrangeItemDragging}` : ''}${dragOverIndex === idx && draggedIndex !== idx ? ` ${styles.arrangeItemOver}` : ''}`}
          draggable
          onDragStart={() => onDragStart(idx)}
          onDragOver={(e) => onDragOver(e, idx)}
          onDrop={() => onDrop(idx)}
          onDragEnd={onDragEnd}
        >
          <div className={styles.arrangeGrip}>
            <GripVertical size={16} />
          </div>
          {slide.imageUrl ? (
            <img
              src={slide.imageUrl}
              alt=''
              className={styles.arrangeThumb}
            />
          ) : (
            <div className={styles.arrangeThumbPlaceholder}>
              <ImageIcon size={14} />
            </div>
          )}
          <div className={styles.arrangeCaption}>
            <strong>{slide.label}</strong>
            <small>
              {slide.sourceType === 'trip_cover'
                ? 'Cover photo'
                : slide.sourceType === 'trip_photo'
                  ? 'Trip photo'
                  : slide.slideType === 'video_clip'
                    ? 'Video clip'
                    : 'Activity'}
            </small>
          </div>
          <button
            type='button'
            className={styles.arrangeRemove}
            onClick={() => onRemove(slide.key)}
            title='Remove slide'
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Step 6: Preview ───────────────────────────────────────────────────────────

interface StepPreviewProps {
  slides: ITripCardSlide[];
}

function StepPreview({ slides }: StepPreviewProps) {
  return (
    <div className={styles.previewStep}>
      {slides.length > 0 ? (
        <div className={styles.previewCard}>
          <TripImageSlideshow
            slides={slides}
            altText='Trip card preview'
          />
        </div>
      ) : (
        <div className={styles.emptyState}>No slides configured yet.</div>
      )}
      <p className={styles.previewNote}>
        This is how your trip card will look. Save to apply the changes.
      </p>
    </div>
  );
}
